import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const LOGO_URL = "https://assets.lolcdn.xyz/logo-2.png";

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'About Us', href: '/#about' },
    { name: 'Internal Systems', href: '/#services' },
    { name: 'Maintenance Plans', href: '/maintenance', isRoute: true },
    { name: 'Instant Quote', href: '/instant-quote', isRoute: true },
    { name: 'Contact', href: '/#contact' },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/90 backdrop-blur-xl border-b border-slate-100/50 shadow-sm' 
            : 'bg-transparent'
        }`}
        data-testid="navigation"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <motion.img
                src={LOGO_URL}
                alt="LOLCorp Logo"
                width="140"
                height="48"
                fetchpriority="high"
                className="h-10 md:h-12 w-auto"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                >
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="nav-link text-sm font-medium uppercase tracking-widest text-[#003333]/70 hover:text-[#003333] transition-colors py-2"
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="nav-link text-sm font-medium uppercase tracking-widest text-[#003333]/70 hover:text-[#003333] transition-colors py-2"
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.name}
                    </a>
                  )}
                </motion.div>
              ))}
              
              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 }}
              >
                <Link
                  to="/consultation"
                  className="bg-[#003333] text-white hover:bg-[#004d4d] rounded-full px-6 py-3 text-sm font-medium transition-all duration-300 hover:scale-105"
                  data-testid="nav-book-consultation"
                >
                  Book a Consultation
                </Link>
              </motion.div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-[#003333]"
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 bg-white z-40 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center h-full gap-8">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-2xl font-semibold text-[#003333]"
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-2xl font-semibold text-[#003333]"
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.name}
                    </a>
                  )}
                </motion.div>
              ))}
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Link
                  to="/consultation"
                  className="bg-[#003333] text-white hover:bg-[#004d4d] rounded-full px-8 py-4 text-lg font-medium transition-all duration-300"
                  data-testid="mobile-nav-book-consultation"
                >
                  Book a Consultation
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
